import { createExitPolicy } from './ExitPolicy'
import { mergeConfig, uid, type PartialLotteryConfig } from './config'
import { DrawStateMachine } from './DrawStateMachine'
import type {
  EventHandler,
  LotteryMachineConfig,
  MachineId,
  DrawSession,
} from './types'
import { MachineWorld } from '../physics/MachineWorld'

export class LotteryController {
  readonly config: LotteryMachineConfig
  private session: DrawSession | null = null
  private viewMachine: MachineId = 'red'
  private redWorld: MachineWorld | null = null
  private blueWorld: MachineWorld | null = null
  private redSm: DrawStateMachine | null = null
  private blueSm: DrawStateMachine | null = null
  private phase: MachineId | 'done' = 'red'
  private handlers: EventHandler[] = []
  private acc = 0

  constructor(partial?: PartialLotteryConfig) {
    this.config = mergeConfig(partial)
  }

  on(handler: EventHandler): () => void {
    this.handlers.push(handler)
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler)
    }
  }

  private emit: EventHandler = (e) => {
    for (const h of this.handlers) h(e)
  }

  getActiveWorld(): MachineWorld | null {
    if (this.viewMachine === 'blue') return this.blueWorld
    return this.redWorld
  }

  getViewMachine(): MachineId {
    return this.viewMachine
  }

  setViewMachine(id: MachineId): void {
    this.viewMachine = id
  }

  getSession(): DrawSession | null {
    return this.session
  }

  getRuntimeState(): string {
    const sm = this.viewMachine === 'blue' ? this.blueSm : this.redSm
    return sm?.runtime.state ?? 'Idle'
  }

  start(): void {
    this.disposeWorlds()
    this.session = {
      sessionId: uid('draw'),
      mode: this.config.mode,
      createdAt: Date.now(),
      result: { red: [], blue: [] },
      phase: 'red',
      status: 'running',
    }
    this.phase = 'red'
    this.emit({
      type: 'session_start',
      sessionId: this.session.sessionId,
      mode: this.config.mode,
    })
    this.emit({ type: 'phase_change', phase: 'red' })

    this.redWorld = new MachineWorld('red', this.config)
    this.blueWorld = new MachineWorld('blue', this.config)
    const policy = createExitPolicy(this.config.mode)

    this.redSm = new DrawStateMachine({
      config: this.config,
      session: this.session,
      machineId: 'red',
      world: this.redWorld,
      policy,
      emit: this.emit,
      getTargetNumber: () => undefined,
      onMachineComplete: (numbers) => {
        if (!this.session) return
        this.session.result.red = numbers
        this.phase = 'blue'
        this.viewMachine = 'blue'
        this.emit({ type: 'phase_change', phase: 'blue' })
        this.blueSm?.start()
      },
    })

    this.blueSm = new DrawStateMachine({
      config: this.config,
      session: this.session,
      machineId: 'blue',
      world: this.blueWorld,
      policy,
      emit: this.emit,
      getTargetNumber: () => undefined,
      onMachineComplete: (numbers) => {
        if (!this.session) return
        this.session.result.blue = numbers
        this.session.phase = 'done'
        this.session.status = 'finished'
        this.phase = 'done'
        this.emit({ type: 'phase_change', phase: 'done' })
        this.emit({ type: 'session_finished', result: { ...this.session.result } })
      },
    })

    this.viewMachine = 'red'
    this.redSm.start()
  }

  reset(): void {
    this.disposeWorlds()
    this.session = null
    this.phase = 'red'
    this.viewMachine = 'red'
    this.acc = 0
  }

  tick(dt: number): void {
    if (!this.session || this.session.status !== 'running') {
      this.redWorld?.step(Math.min(dt, 0.05))
      this.blueWorld?.step(Math.min(dt, 0.05))
      return
    }

    this.acc += dt
    const fixed = this.config.fixedDt
    let steps = 0
    while (this.acc >= fixed && steps < this.config.maxSubSteps) {
      this.acc -= fixed
      steps += 1
      if (this.phase === 'red') {
        this.redWorld?.step(fixed)
        this.redSm?.update(fixed)
      } else if (this.phase === 'blue') {
        this.blueWorld?.step(fixed)
        this.blueSm?.update(fixed)
      }
    }
  }

  private disposeWorlds(): void {
    this.redWorld?.dispose()
    this.blueWorld?.dispose()
    this.redWorld = null
    this.blueWorld = null
    this.redSm = null
    this.blueSm = null
  }
}
